export function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-6 md:mb-8 gap-4 md:gap-6">
      <div className="min-w-0">
        {eyebrow && <div className="label-eyebrow mb-2">{eyebrow}</div>}
        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight leading-tight md:leading-none break-words">{title}</h1>
        {subtitle && <p className="mt-3 text-sm md:text-base text-neutral-500 max-w-xl">{subtitle}</p>}
      </div>
      {actions && <div className="flex gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, hint, testid, tone = "default", children }) {
  return (
    <div data-testid={testid} className="border border-[#E2D9C8] rounded-lg p-5 bg-white">
      <div className="label-eyebrow">{label}</div>
      <div className={`mt-2 font-serif text-3xl leading-none tracking-tight ${tone === "danger" ? "text-red-700" : tone === "good" ? "text-emerald-800" : tone === "muted" ? "text-neutral-400" : ""}`}>
        {value}
      </div>
      {hint && <div className="text-xs text-neutral-500 mt-2">{hint}</div>}
      {children}
    </div>
  );
}

/**
 * TrustBadge — accessible confidence indicator (spec §10).
 *
 * Combines a colored dot + explicit text label (colour is NOT the only
 * signifier so it remains accessible under colour-blindness). Tooltip
 * (native `title`) exposes source/coverage/reason.
 *
 * level:
 *   "high"        → Affidabile   (verde)
 *   "medium"      → Parziale     (ambra)
 *   "low"         → Bassa        (arancione)
 *   "insufficient"→ Insufficiente (rosso)
 *   "confirmed"   → Confermato   (verde, per KPI puntuali)
 *   "provisional" → Provvisorio  (ambra)
 *   "na"          → N/A          (grigio)
 */
export function TrustBadge({ level = "na", label, tooltip, testid }) {
  const map = {
    high:         { cls: "bg-emerald-100 text-emerald-800 border-emerald-300",  dot: "bg-emerald-600", text: label || "Affidabile" },
    confirmed:    { cls: "bg-emerald-100 text-emerald-800 border-emerald-300",  dot: "bg-emerald-700", text: label || "Confermato" },
    medium:       { cls: "bg-amber-50 text-amber-800 border-amber-300",         dot: "bg-amber-500",   text: label || "Parziale" },
    provisional:  { cls: "bg-amber-50 text-amber-800 border-amber-300",         dot: "bg-amber-500",   text: label || "Provvisorio" },
    low:          { cls: "bg-orange-50 text-orange-800 border-orange-300",      dot: "bg-orange-500",  text: label || "Bassa" },
    insufficient: { cls: "bg-red-50 text-red-800 border-red-300",               dot: "bg-red-600",     text: label || "Insufficiente" },
    na:           { cls: "bg-neutral-100 text-neutral-600 border-neutral-300",  dot: "bg-neutral-400", text: label || "N/A" },
  };
  const s = map[level] || map.na;
  return (
    <span
      data-testid={testid}
      title={tooltip || s.text}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-medium leading-none ${s.cls}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} aria-hidden="true" />
      {s.text}
    </span>
  );
}

export const fmtEUR = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const fmtInt = (n) => Number(n || 0).toLocaleString("it-IT");
